import React, { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BinScatterChart } from 'apps/sigma2-ui/src/components/BinScatterChart';
import { IconChevronLeftSvg } from 'apps/sigma2-ui/src/components/svg/IconChevronLeft';
import { IconDownloadSvg } from 'apps/sigma2-ui/src/components/svg/IconDownload';
import { Divider } from 'apps/sigma2-ui/src/components/ui/Divider';
import { Heading } from 'apps/sigma2-ui/src/components/ui/Heading';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { DisparityBarChart } from 'apps/sigma2-ui/src/pages/FocusAreaPage/DisparityBarChart';
import { RootCauseGenericBarChart } from 'apps/sigma2-ui/src/pages/FocusAreaPage/RootCauseGenericBarChart';
import { UppercaseFirst } from 'apps/sigma2-ui/src/lib/translations/translation-utils';
import { getInsightFormatOrDefault } from 'libs/end-to-end/src/insights';
import { getInsightWithReferenceFormatOrDefault } from 'libs/end-to-end/src/insights';
import { roughSanitizeHtml } from 'libs/end-to-end/src/html';
import { Insight } from 'libs/end-to-end/src/insights';
import { isDiversionBarGraphGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { yAxisDivergedBarGraph } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseBinScatterControlledVsUncontrolledGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseDivergedBarGraphGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { RootCauseGroupResult } from 'libs/end-to-end/src/root-cause-analysis';
import { getDateAsYYYYMMDD } from 'libs/end-to-end/src/dates';
import { LoadingSkeleton } from 'apps/sigma2-ui/src/components/LoadingSkeleton';
import { fillArray } from 'libs/end-to-end/src/factories/fillArray';
import { DownloadButton } from 'apps/sigma2-ui/src/components/ui/DownloadButton';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { capitalize } from 'lodash';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { GraphLegend } from 'apps/sigma2-ui/src/components/ui/GraphLegend';
import { getControlVariableDisplayText } from 'apps/sigma2-ui/src/pages/FocusAreaPage/RootCauseDisparitySection';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Link } from 'react-router-dom';
import { CalibrationFraming } from 'libs/end-to-end/src/calibration-framing';
import { getTextOrExplanation } from 'apps/sigma2-ui/src/disparityAndOptimization/components/DisparitySection';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';
import { MyMetricDefinition } from 'libs/end-to-end/src/my-metric-definition';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';

const BackToAllInsightsButton: React.FC = () => {
  const language = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();

  return (
    <Spacing
      direction="horizontal"
      size="sm"
      className="w-full html-to-image-filter"
    >
      <button
        onClick={() => {
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('dimension');
          newParams.delete('treatmentLevel');
          newParams.delete('type');
          setSearchParams(newParams);
        }}
      >
        <Spacing direction="horizontal" size="md" className="items-center">
          <span className="bg-white rounded-full p-1">
            <IconChevronLeftSvg
              svg={{
                className: 'w-5 h-5',
              }}
            />
          </span>

          <S2Typography
            variant="body-7"
            translatedContent={language.insightBackToAllInsights()}
          />
        </Spacing>
      </button>
    </Spacing>
  );
};

/* TODO remove the dependency on the share link only working for copilots */
const InsightDetailsActions: React.FC<{
  downloadContainerRef: React.RefObject<HTMLDivElement>;
}> = ({ downloadContainerRef }) => {
  const language = useLanguage();

  const analytics = useAnalytics();

  const onDownloadCb = useCallback(() => {
    analytics.trackEvent({
      eventName: 'Chart Downloaded',
      eventProperties: {
        type: 'Insight Details',
      },
    });
  }, [analytics]);

  return (
    <Spacing direction="horizontal" size="md" className="text-gray-600">
      {/* <button> */}
      {/*   <Spacing */}
      {/*     direction="horizontal" */}
      {/*     size="xs" */}
      {/*     className="items-center html-to-image-filter" */}
      {/*   > */}
      {/*     Share */}
      {/*     <IconShareSvg */}
      {/*       svg={{ */}
      {/*         className: 'w-4 h-4', */}
      {/*       }} */}
      {/*     /> */}
      {/*   </Spacing> */}
      {/* </button> */}

      <DownloadButton
        getElement={() => {
          return downloadContainerRef.current;
        }}
        filename={`insight-details-${getDateAsYYYYMMDD(new Date())}`}
        onDownloadCb={onDownloadCb}
      >
        <Spacing direction="horizontal" size="xs" className="items-center">
          {convertTranslatedContentToReactNode(language.insightDownload())}
          <IconDownloadSvg
            svg={{
              className: 'w-4 h-4',
            }}
          />
        </Spacing>
      </DownloadButton>
    </Spacing>
  );
};

const GraphExplanation: React.FC<{
  groupResult: Insight['groupResults'][0];
  focusAreaDefinition: MyFocusAreaDefinition;
  metricDefinition: MyMetricDefinition;
  numerator: number;
}> = ({ groupResult, numerator, focusAreaDefinition, metricDefinition }) => {
  const language = useLanguage();

  const descriptionHtml = capitalize(
    getTextOrExplanation({
      groupResult,
      focusAreaDefinition,
      agentReference: metricDefinition.whiteLabeling.subjectPlural,
    }),
  );

  return (
    <Spacing direction="vertical" size="sm">
      <Heading
        level={2}
        textSize="text-xl"
        fontWeight="font-black"
        fontFamily="font-sans"
      >
        {convertTranslatedContentToReactNode(
          language.insightDetailsGraphExplanationTitle({
            groupResult,
            numerator,
          }),
        )}
      </Heading>

      <UppercaseFirst>
        <Paragraph>
          <span
            dangerouslySetInnerHTML={{
              __html: roughSanitizeHtml(descriptionHtml),
            }}
          />
        </Paragraph>
      </UppercaseFirst>
    </Spacing>
  );
};

const DisparityBarChartSection: React.FC<{
  insight: Insight;
  result: RootCauseDivergedBarGraphGroupResult;
  numerator: number;
  treatmentLevel: string;
  focusArea: MyFocusAreaDefinition;
  metricDefinition: MyMetricDefinition;
}> = ({ numerator, treatmentLevel, result, focusArea, metricDefinition }) => {
  const language = useLanguage();
  const controlVariable = getControlVariableDisplayText(
    result,
    focusArea.controlVariables,
  );

  return (
    <Spacing direction="vertical" size="sm">
      <GraphExplanation
        groupResult={result}
        numerator={numerator}
        focusAreaDefinition={focusArea}
        metricDefinition={metricDefinition}
      />

      <Spacing direction="horizontal" size="lg" className="justify-center">
        <GraphLegend
          color="#076DD3"
          key="insightDisparityBarChartWithoutControlVariable"
        >
          {convertTranslatedContentToReactNode(
            language.insightDisparityBarChartWithoutControlVariable({
              controlVariable,
            }),
          )}
        </GraphLegend>

        <GraphLegend
          color="#CE106F"
          key="insightDisparityBarChartWithControlVariable"
        >
          {convertTranslatedContentToReactNode(
            language.insightDisparityBarChartWithControlVariable({
              controlVariable,
            }),
          )}
        </GraphLegend>
      </Spacing>

      <div className="h-96">
        <DisparityBarChart
          result={result}
          controlVariable={result.control_variable}
          demographicKey={treatmentLevel}
          yAxisText={yAxisDivergedBarGraph(focusArea)}
        />
      </div>
    </Spacing>
  );
};

const DisparityBinScatterSection: React.FC<{
  dimension: string;
  groupResult: RootCauseBinScatterControlledVsUncontrolledGroupResult;
  numerator: number;
  treatmentLevel: string;
  referenceLevel: string;
  focusAreaDefinition: MyFocusAreaDefinition;
  metricDefinition: MyMetricDefinition;
}> = ({
  dimension,
  groupResult,
  numerator,
  focusAreaDefinition,
  metricDefinition,
}) => {
  return (
    <Spacing direction="vertical" size="sm">
      <GraphExplanation
        groupResult={groupResult}
        numerator={numerator}
        focusAreaDefinition={focusAreaDefinition}
        metricDefinition={metricDefinition}
      />

      <div className="h-96">
        {metricDefinition && (
          <BinScatterChart
            groupResult={groupResult}
            dimension={dimension}
            focusArea={focusAreaDefinition}
            metricDefinition={metricDefinition}
            overridingYAxisText={null}
            overridingXAxisText={null}
          />
        )}
      </div>
    </Spacing>
  );
};

export const InsightDetailsGraph: React.FC<{
  dimension: string;
  insight: Insight;
  rootCauseGroupResult: RootCauseGroupResult;
  numerator: number;
  focusAreaDefinition: MyFocusAreaDefinition;
}> = ({
  dimension,
  rootCauseGroupResult,
  numerator,
  insight,
  focusAreaDefinition,
}) => {
  const metricDefinition = useMemo(() => {
    return focusAreaDefinition.metrics.find(
      (metric) => metric.columnHeaderValue === insight.metric,
    );
  }, [focusAreaDefinition, insight]);
  return (
    <>
      {metricDefinition && (
        <div className="bg-gray-100 min-h-80 w-full max-w-3xl p-5">
          {isDiversionBarGraphGroupResult(rootCauseGroupResult) && (
            <DisparityBarChartSection
              insight={insight}
              numerator={numerator}
              treatmentLevel={insight.treatmentLevel}
              result={rootCauseGroupResult}
              focusArea={focusAreaDefinition}
              metricDefinition={metricDefinition}
            />
          )}

          {rootCauseGroupResult.plot_type ===
            'BinScatterControlledVsUncontrolled' && (
            <DisparityBinScatterSection
              groupResult={rootCauseGroupResult}
              dimension={dimension}
              numerator={numerator}
              referenceLevel={insight.referenceLevel}
              treatmentLevel={insight.treatmentLevel}
              focusAreaDefinition={focusAreaDefinition}
              metricDefinition={metricDefinition}
            />
          )}

          {rootCauseGroupResult.plot_type === 'GenericBarGraph' && (
            <Spacing direction="vertical" size="sm">
              <GraphExplanation
                groupResult={rootCauseGroupResult}
                numerator={numerator}
                focusAreaDefinition={focusAreaDefinition}
                metricDefinition={metricDefinition}
              />

              <div>
                <RootCauseGenericBarChart
                  result={rootCauseGroupResult}
                  treatmentLevel={insight.treatmentLevel}
                  download={<></>}
                  yAxisText={yAxisDivergedBarGraph(focusAreaDefinition)}
                  graphClassName={'h-96'}
                />
              </div>
            </Spacing>
          )}
        </div>
      )}
    </>
  );
};

const InsightDetailsHeading: React.FC<{
  insight: Insight;
  focusArea: MyFocusAreaDefinition;
  actions: React.ReactNode;
  calibrationFraming: CalibrationFraming;
}> = ({ insight, actions, focusArea, calibrationFraming }) => {
  const language = useLanguage();
  const { getDimensionByColumnHeaderValue, getLevelByColumnValue } =
    useDimensionConfig();

  const dimensionDefinition = useMemo(() => {
    return getDimensionByColumnHeaderValue(insight.dimension);
  }, [getDimensionByColumnHeaderValue, insight.dimension]);

  const treatmentDefinition = useMemo(() => {
    return getLevelByColumnValue(insight.dimension, insight.treatmentLevel);
  }, [getLevelByColumnValue, insight.dimension, insight.treatmentLevel]);

  const referenceDefinition = getLevelByColumnValue(
    insight.dimension,
    insight.referenceLevel,
  );

  const metricDefinition = useMemo(() => {
    return focusArea.metrics.find(
      (metric) => metric.columnHeaderValue === insight.metric,
    );
  }, [focusArea, insight]);

  const showBeforeEmpsOrApps =
    dimensionDefinition?.showBeforeEmpsOrApps ?? false;

  const subjectDisplay = metricDefinition?.whiteLabeling.subjectPlural ?? '';

  const insightFormat = getInsightFormatOrDefault(
    dimensionDefinition?.insightFormat,
  );
  const insightWithReferenceFormat = getInsightWithReferenceFormatOrDefault(
    dimensionDefinition?.insightWithReferenceFormat,
  );

  const insightDisplay = language
    .insightDisplay({
      showBeforeEmpsOrApps,
      empsOrAppsDisplay: subjectDisplay,
      insightFormat,
      levelDisplay: treatmentDefinition?.displayName ?? '',
    })
    .toLocaleLowerCase();

  const levelDisplay =
    getLevelByColumnValue(
      insight.dimension,
      insight.treatmentLevel,
    )?.displayName.toLocaleLowerCase() ?? '';

  const referenceDisplay =
    referenceDefinition?.displayName.toLocaleLowerCase() ?? '';

  const insightWithReferenceDisplay = getTranslatedContentAsString(
    language.insightWithReferenceDisplay({
      insightWithReferenceFormat,
      levelDisplay,
      empsOrAppsDisplay: subjectDisplay,
      showBeforeEmpsOrApps,
      referenceDisplay,
    }),
  ).toLocaleLowerCase();

  const focusAreaDisplay = focusArea.displayName.toLocaleLowerCase();

  const lowerHigherDisplay = getTranslatedContentAsString(
    language.lowerHigherDisplay({
      type: insight?.highlight?.type ?? '',
    }),
  ).toLocaleLowerCase();

  return (
    <Spacing direction="vertical" size="lg" className="w-full items-center">
      <div className="w-full min-w-screen">
        <BackToAllInsightsButton />
      </div>

      <Spacing
        direction="horizontal"
        size="md"
        className="justify-between w-full max-w-3xl"
      >
        <S2Typography
          variant="headline-5"
          translatedContent={language.insightType({
            insight,
            levelDisplay: treatmentDefinition?.displayName ?? '',
          })}
        />

        {actions}
      </Spacing>

      <Spacing
        direction="vertical"
        size="sm"
        className="items-center max-w-3xl"
      >
        <Spacing direction="vertical" size="sm">
          {/* TODO do we need to accomodate strong text in the title */}
          <S2Typography
            variant="headline-3"
            translatedContent={language.insightTitleNew({
              phrases: language,
              insightDisplay,
              insightWithReferenceDisplay,
              focusAreaDisplay,
              insight,
              calibrationFraming,
            })}
          />

          <S2Typography
            variant="body-5"
            translatedContent={language.insightTextNew({
              phrases: language,
              insight,
              focusAreaDisplay,
              insightDisplay,
              insightWithReferenceDisplay,
              lowerHigherDisplay,
              calibrationFraming,
            })}
            dangerouslyOverrideClassNameFn={(classNames) =>
              classNames + ' first-letter:uppercase'
            }
          />
        </Spacing>
      </Spacing>
    </Spacing>
  );
};

export const InsightDetails: React.FC<{
  insight: Insight | null;
  relatedInsightsSection: React.ReactNode;
  focusArea: MyFocusAreaDefinition;
  isLoading: boolean;
}> = ({ insight, relatedInsightsSection, focusArea, isLoading }) => {
  const language = useLanguage();

  const downloadContainerRef = useRef<HTMLDivElement>(null);

  const observationHook = useObservation();

  const insightNotFound = !isLoading && !insight;

  if (insightNotFound) {
    return (
      <Spacing direction="vertical" size="sm">
        <S2Typography
          variant="headline-3"
          translatedContent={language.insightNotFoundHeadline()}
        />

        <S2Typography
          variant="body-7"
          translatedContent={language.insightNotFoundBody()}
        />

        <Link to={`/${focusArea.focusArea}/insights`}>
          <S2Typography
            variant="link"
            translatedContent={language.insightBackToAllInsights()}
          />
        </Link>
      </Spacing>
    );
  }

  if (!observationHook.myObservation) {
    return <></>;
  }

  return (
    <Spacing direction="vertical" size="gap-10" className="items-center">
      <UserAnalysesOverlay onUserAnalysisCancel={() => {}} />
      {!isLoading && insight && (
        <Spacing
          direction="vertical"
          size="gap-10"
          className="items-center"
          ref={downloadContainerRef}
        >
          <InsightDetailsHeading
            focusArea={focusArea}
            insight={insight}
            actions={
              <InsightDetailsActions
                downloadContainerRef={downloadContainerRef}
              />
            }
            calibrationFraming={
              observationHook.myObservation.calibrationFraming
            }
          />

          {insight.groupResults.map((groupResult, index) => {
            return (
              <InsightDetailsGraph
                dimension={insight.dimension}
                insight={insight}
                key={`insight-${index.toString()}`}
                rootCauseGroupResult={groupResult}
                numerator={index + 1}
                focusAreaDefinition={focusArea}
              />
            );
          })}
        </Spacing>
      )}

      {isLoading && (
        <Spacing
          direction="vertical"
          size="gap-10"
          className="w-full max-w-3xl"
        >
          <BackToAllInsightsButton />

          <S2Typography
            variant="headline-3"
            translatedContent={language.insight()}
          />

          <LoadingSkeleton heightClassName="h-32" />
          <LoadingSkeleton heightClassName="h-64" />

          {fillArray({
            length: 3,
            valueFn() {
              return null;
            },
          }).map((_, index) => {
            return <LoadingSkeleton heightClassName="h-32" key={index} />;
          })}
        </Spacing>
      )}

      <div className="max-w-3xl w-full">
        <Divider direction="horizontal" />
      </div>

      {relatedInsightsSection}
    </Spacing>
  );
};
